import { useState } from "react";
import { FileText, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Excel round-trip import: choose workbook -> validate (server preview,
// no writes) -> review changes -> import (server re-validates the same
// file and applies it in one transaction) -> results.

const fmt = (field, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "is_active") return v ? "Yes" : "No";
  if (field === "current_selling_price") return Number(v).toFixed(2);
  return String(v);
};

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (err?.code === "ECONNABORTED") return `${fallback} The request timed out — please try again.`;
  return `${fallback} Please try again. If it keeps failing, contact support.`;
};

const btn = "inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50";

export default function ProductImportModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const reset = () => { setFile(null); setPreview(null); setResult(null); setBusy(""); setError(""); };
  const close = () => { if (busy) return; if (result) onImported?.(); reset(); onClose(); };

  const send = (path, extra = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
  };

  const validate = async () => {
    setBusy("validate"); setError(""); setPreview(null);
    try { setPreview((await send("/mgmt/products/excel/preview")).data); }
    catch (err) { setError(errText(err, "The workbook could not be validated.")); }
    setBusy("");
  };

  const apply = async () => {
    setBusy("apply"); setError("");
    try {
      const r = await send("/mgmt/products/excel/apply", { token: preview.token });
      if (!r.data?.success) throw new Error("not confirmed");
      setResult(r.data.summary);
    } catch (err) {
      setError(errText(err, "Nothing was imported."));
    }
    setBusy("");
  };

  const s = preview?.summary;
  const hasChanges = s && (s.updates > 0 || s.creates > 0);

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) close(); }}>
      <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="product-import-modal">
        <DialogHeader>
          <DialogTitle>Import Product Catalog from Excel</DialogTitle>
          <DialogDescription>
            Use a workbook made with Export Excel. Rows are matched by Product ID; rows with a blank Product ID become new products.
            Removing a row never deletes a product — set Active to No instead. Nothing is saved until you click Import.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="product-import-result">
            <div className="flex items-center gap-2 text-emerald-700 font-black uppercase tracking-wider"><CheckCircle size={20} weight="fill" /> Import complete</div>
            <div className="grid grid-cols-4 gap-3 text-center">
              {[["Updated", result.updated], ["Created", result.created], ["Unchanged", result.unchanged], ["Failed", result.failed]].map(([l, n]) => (
                <div key={l} className="border border-[#E5E5E5] p-3"><p className="text-2xl font-black">{n}</p><p className="text-xs text-[#A3A3A3] uppercase tracking-wider">{l}</p></div>
              ))}
            </div>
            <div className="flex justify-end"><button onClick={close} className={`${btn} bg-[#0055FF] text-white hover:bg-[#003ECB]`}>Done</button></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <label className={`${btn} border border-[#E5E5E5] hover:bg-[#F5F5F5] cursor-pointer`}>
                <FileText size={15} /> Choose workbook
                <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden"
                  data-testid="product-import-file"
                  onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setError(""); e.target.value = ""; }} />
              </label>
              <span className="text-sm text-[#525252] truncate max-w-xs">{file ? file.name : "No file chosen"}</span>
              <button onClick={validate} disabled={!file || !!busy} className={`${btn} bg-[#0A0A0A] text-white hover:bg-[#262626]`} data-testid="product-import-validate">
                {busy === "validate" ? "Validating..." : "Validate"}
              </button>
            </div>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3" data-testid="product-import-error">{error}</div>}

            {s && (
              <>
                <div className="grid grid-cols-5 gap-2 text-center" data-testid="product-import-summary">
                  {[["Rows read", s.rows_read], ["Updates", s.updates], ["New products", s.creates], ["Unchanged", s.unchanged], ["Rows with errors", s.errors]].map(([l, n]) => (
                    <div key={l} className={`border p-2 ${l === "Rows with errors" && n > 0 ? "border-red-300 bg-red-50" : "border-[#E5E5E5]"}`}>
                      <p className="text-xl font-black">{n}</p><p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">{l}</p>
                    </div>
                  ))}
                </div>

                {preview.errors.length > 0 && (
                  <div className="border border-red-200">
                    <p className="px-3 py-2 bg-red-50 text-red-700 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"><WarningCircle size={14} /> Fix these in the workbook, save, and validate again — nothing can be imported until every row is valid</p>
                    <ul className="divide-y divide-[#F5F5F5] text-sm max-h-60 overflow-y-auto">
                      {preview.errors.map((e, i) => (
                        <li key={i} className="px-3 py-1.5">Row {e.row}{e.product ? ` — ${e.product}` : ""} — <span className="font-semibold">{e.field}</span>: {e.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {preview.updates.length > 0 && (
                  <div className="border border-[#E5E5E5]">
                    <p className="px-3 py-2 bg-[#FAFAFA] text-xs font-bold uppercase tracking-wider text-[#525252]">Changes to existing products</p>
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs text-[#A3A3A3]"><th className="px-3 py-1">Row</th><th className="px-3 py-1">Product</th><th className="px-3 py-1">Field</th><th className="px-3 py-1">Before</th><th className="px-3 py-1">After</th></tr></thead>
                      <tbody className="divide-y divide-[#F5F5F5]">
                        {preview.updates.flatMap(u => u.changes.map((c, i) => (
                          <tr key={`${u.product_id}-${c.field}`} className="align-top">
                            <td className="px-3 py-1 text-[#A3A3A3]">{i === 0 ? u.row : ""}</td>
                            <td className="px-3 py-1 font-semibold">{i === 0 ? `${u.product_code} — ${u.name}` : ""}</td>
                            <td className="px-3 py-1 text-[#525252]">{c.label}</td>
                            <td className="px-3 py-1 text-[#A3A3A3] line-through break-words max-w-xs">{fmt(c.field, c.before)}</td>
                            <td className="px-3 py-1 text-[#0A0A0A] break-words max-w-xs">{fmt(c.field, c.after)}</td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                )}

                {preview.creates.length > 0 && (
                  <div className="border border-[#E5E5E5]">
                    <p className="px-3 py-2 bg-[#FAFAFA] text-xs font-bold uppercase tracking-wider text-[#525252]">New products</p>
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs text-[#A3A3A3]"><th className="px-3 py-1">Row</th><th className="px-3 py-1">Code</th><th className="px-3 py-1">Name</th><th className="px-3 py-1">Category</th><th className="px-3 py-1">Pricing</th><th className="px-3 py-1 text-right">Price</th><th className="px-3 py-1">Active</th></tr></thead>
                      <tbody className="divide-y divide-[#F5F5F5]">
                        {preview.creates.map(c => (
                          <tr key={c.row}>
                            <td className="px-3 py-1 text-[#A3A3A3]">{c.row}</td>
                            <td className="px-3 py-1 font-mono text-xs">{c.product_code}</td>
                            <td className="px-3 py-1">{c.name}</td>
                            <td className="px-3 py-1 text-[#525252]">{c.category}</td>
                            <td className="px-3 py-1 text-[#525252]">{c.pricing_type}</td>
                            <td className="px-3 py-1 text-right font-mono">{c.price_currency_code} {fmt("current_selling_price", c.current_selling_price)}</td>
                            <td className="px-3 py-1">{fmt("is_active", c.is_active)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {!hasChanges && preview.errors.length === 0 && (
                  <p className="text-sm text-[#525252]">No changes found — the workbook matches the current catalog.</p>
                )}
              </>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={close} disabled={!!busy} className={`${btn} border border-[#E5E5E5] hover:bg-[#F5F5F5]`}>Cancel</button>
              <button onClick={apply} disabled={!hasChanges || preview.errors.length > 0 || !!busy}
                className={`${btn} bg-[#0055FF] text-white hover:bg-[#003ECB]`} data-testid="product-import-apply">
                {busy === "apply" ? "Importing..." : hasChanges ? `Import ${s.updates + s.creates} change${s.updates + s.creates !== 1 ? "s" : ""}` : "Import"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
